import BaseDrawer from "@stardust/base-drawer";

class MobileFiltersDrawer extends BaseDrawer {
  constructor(element) {
    super(element, {
      openClass: 'opacity-0',
      closeClass: 'opacity-0',
    });
    // Bumped on every open() and every hard-close (see the morph listener in
    // load()). Lets a close's deferred finalizeClose detect that a hard-close
    // or a later reopen already moved this instance to a different session,
    // so it doesn't call super.close() on a session it doesn't own.
    this._session = 0;
  }

  open() {
    this._session++;
    super.open();
    const form = this.element.querySelector('[data-mobile-filters-form]');
    if (form) {
      requestAnimationFrame(() => form.classList.remove('translate-y-full'));
    }
    document.querySelectorAll('[aria-controls="MobileFiltersDrawer"]').forEach(el => el.setAttribute('aria-expanded', 'true'));
  }

  close() {
    const session = this._session;
    const form = this.element.querySelector('[data-mobile-filters-form]');
    if (form) {
      let hasClosed = false;
      let fallbackTimeout;
      const finalizeClose = () => {
        if (hasClosed) return;
        hasClosed = true;
        clearTimeout(fallbackTimeout);
        form.removeEventListener('transitionend', onEnd);
        // Stale if a hard-close or a reopen already moved this instance to a
        // different session — that session owns its own overlay/focus-trap/
        // scroll-lock cleanup, so calling super.close() here would tear it down.
        if (session !== this._session) return;
        super.close();
      };
      form.classList.add('translate-y-full');
      const onEnd = (e) => {
        // Tailwind v4 emits translate utilities as the `translate` property, not `transform`.
        if (e.target !== form || !['transform', 'translate'].includes(e.propertyName)) return;
        finalizeClose();
      };
      form.addEventListener('transitionend', onEnd);
      fallbackTimeout = window.setTimeout(finalizeClose, 400);
    } else {
      super.close();
    }
    document.querySelectorAll('[aria-controls="MobileFiltersDrawer"]').forEach(el => el.setAttribute('aria-expanded', 'false'));
  }

  load() {
    // Listen for open/close events dispatched by theme.js event delegation.
    // Both use document-level listeners so they survive DOM morphs.
    document.addEventListener("mobile-filters:open", this._boundOpen);
    document.addEventListener("mobile-filters:close", this._boundClose);

    // Close when clicking the backdrop (the drawer element itself, not the form panel inside).
    this.element.addEventListener('click', (e) => {
      if (e.target === this.element) this.close();
    });

    // A morph can land while the form's close transition is still pending, or
    // while the drawer is fully open. Hard-close immediately — the DOM is
    // about to be replaced, so there's nothing to wait for a transition on —
    // and bump the session so a manual close already in flight can't call
    // super.close() again later and tear down a reopen that happens in between.
    document.addEventListener('theme:section:morph', () => {
      if (!this.isOpen) return;
      this._session++;
      BaseDrawer.prototype.close.call(this);
      document.querySelectorAll('[aria-controls="MobileFiltersDrawer"]').forEach(el => el.setAttribute('aria-expanded', 'false'));
    });

    // Allow SS (or any provider) to replace the drawer's inner content
    document.addEventListener('mobile-filters:set-content', (e) => {
      const contentArea = this.element?.querySelector('[data-mobile-filters-content]');
      if (contentArea && e.detail?.content) {
        contentArea.innerHTML = '';
        contentArea.appendChild(e.detail.content);
      }
    });
  }
}

// Initialize mobile filters drawer
const init = () => {
  const mobileFiltersDrawer = document.querySelector('[data-mobile-filters-drawer]');
  if (mobileFiltersDrawer) new MobileFiltersDrawer(mobileFiltersDrawer).load();
};
init();
